import { motion } from 'framer-motion';
import {
  AlertCircle,
  ArrowLeft,
  Briefcase,
  Building,
  CheckCircle,
  Mail,
  Phone,
  Plus,
  Save,
  Trash2,
  User
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { employeeAPI } from '../../utils/api';

const departments = [
  'Engineering',
  'Marketing', 
  'Sales',
  'HR',
  'Finance',
  'Operations',
  'Other'
];

const permissions = [
  { id: 'view_candidates', label: 'View Candidates', description: 'Can view candidate profiles and applications' },
  { id: 'manage_candidates', label: 'Manage Candidates', description: 'Can update candidate status and add notes' },
  { id: 'view_jobs', label: 'View Jobs', description: 'Can view job postings and details' },
  { id: 'manage_jobs', label: 'Manage Jobs', description: 'Can create, edit, and manage job postings' },
  { id: 'view_applications', label: 'View Applications', description: 'Can view job applications' },
  { id: 'manage_applications', label: 'Manage Applications', description: 'Can review and process applications' },
  { id: 'view_reports', label: 'View Reports', description: 'Can access reporting and analytics' },
  { id: 'manage_reports', label: 'Manage Reports', description: 'Can generate and export reports' }
];

const EmployeeForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    department: '',
    hourlyRate: '',
    clientName: '',
    projects: [],
    permissions: {}
  });

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [projectInput, setProjectInput] = useState('');

  useEffect(() => {
    if (isEditing) {
      fetchEmployee();
    }
  }, [id, isEditing]);

  const fetchEmployee = async () => {
    try {
      setLoading(true);
      const response = await employeeAPI.getEmployee(id);
      const employee = response.data.data;
      
      setFormData({
        firstName: employee.firstName || '',
        lastName: employee.lastName || '',
        email: employee.email || '',
        phone: employee.phone || '',
        position: employee.position || '',
        department: employee.department || '',
        hourlyRate: employee.hourlyRate || '',
        clientName: employee.clientName || '',
        projects: employee.projects || [],
        permissions: {
          view_candidates: employee.permissions?.viewCandidates || false,
          manage_candidates: employee.permissions?.manageCandidates || false,
          view_jobs: employee.permissions?.viewJobs || false,
          manage_jobs: employee.permissions?.manageJobs || false,
          view_applications: employee.permissions?.viewApplications || false,
          manage_applications: employee.permissions?.manageApplications || false,
          view_reports: employee.permissions?.viewReports || false,
          manage_reports: employee.permissions?.manageReports || false
        }
      });
    } catch (err) {
      console.error('Error fetching employee:', err);
      setError('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for hourly rate to ensure it's a valid number
    if (name === 'hourlyRate') {
      // Allow only numbers and decimal point
      const numericValue = value.replace(/[^0-9.]/g, '');
      // Ensure only one decimal point
      const parts = numericValue.split('.');
      const formattedValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;
      
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear messages when user starts typing
    if (error) setError(null);
    if (message) setMessage(null);
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => ({
          ...prev,
      permissions: {
        ...prev.permissions,
        [permissionId]: !prev.permissions[permissionId]
      }
    }));
  };

  const validateForm = () => {
    const errors = [];
    
    if (!formData.firstName.trim()) {
      errors.push('First name is required');
    }
    
    if (!formData.lastName.trim()) {
      errors.push('Last name is required');
    }
    
    if (!formData.email.trim()) {
      errors.push('Email is required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.push('Please enter a valid email address');
    }
    
    if (!formData.position.trim()) {
      errors.push('Position is required');
    }



    if (!formData.clientName.trim()) {
      errors.push('Client name is required');
    }

    if (formData.hourlyRate === '' || isNaN(parseFloat(formData.hourlyRate)) || parseFloat(formData.hourlyRate) < 0) {
      errors.push('Please enter a valid hourly rate (minimum $0.00)');
    }

    if (!formData.projects || formData.projects.length === 0) {
      errors.push('At least one project must be added');
    }

    if (errors.length > 0) {
      setError(errors.join(', '));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    setError(null);
    setMessage(null);

    try {
      const submitData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        position: formData.position.trim(),
        department: formData.department,
        hourlyRate: parseFloat(formData.hourlyRate),
        clientName: formData.clientName,
        projects: formData.projects,
        permissions: formData.permissions
      };

      let response;
      if (isEditing) {
        response = await employeeAPI.updateEmployee(id, submitData);
        setMessage('Employee updated successfully!');
      } else {
        response = await employeeAPI.createEmployee(submitData);
        setMessage('Employee created successfully! Default password is: 12345678');
      }

      console.log('Employee operation successful:', response.data);

      // Redirect after a short delay
    setTimeout(() => {
        navigate('/employees');
      }, 2000);

    } catch (err) {
      console.error('Error saving employee:', err);
      setError(err.response?.data?.message || 'Failed to save employee');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
  return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-600">Loading employee data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={() => navigate('/employees')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Edit Employee' : 'Add New Employee'}
            </h1>
            <p className="text-gray-600">
              {isEditing ? 'Update employee information and permissions' : 'Create a new employee account with default password'}
            </p>
          </div>
        </div>
      </div>

      {/* Messages */}
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2"
        >
          <CheckCircle size={20} className="text-green-600" />
          <span className="text-green-800">{message}</span>
      </motion.div>
      )}

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2"
        >
          <AlertCircle size={20} className="text-red-600" />
          <span className="text-red-800">{error}</span>
        </motion.div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Personal Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <User size={20} className="mr-2 text-primary-600" />
            Personal Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name *
              </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter first name"
                required
                />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name *
              </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter last name"
                required
                />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail size={18} className="text-gray-400" />
                </div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter email address"
                  required
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone size={18} className="text-gray-400" />
                </div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter phone number"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Job Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <Briefcase size={20} className="mr-2 text-primary-600" />
            Job Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Position *
              </label>
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter job position"
                required
                />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Department
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building size={18} className="text-gray-400" />
                </div>
                <select
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select department (optional)</option>
                  {departments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hourly Rate (USD) *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500">$</span>
                </div>
                <input
                  type="text"
                  name="hourlyRate"
                  value={formData.hourlyRate}
                  onChange={handleChange}
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="25.00"
                  required
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 text-sm">per hour</span>
                </div>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                This rate will be used for timesheet calculations and invoicing
              </p>
            </div>
          </div>
        </div>
        
        {/* Client & Projects */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Client &amp; Projects</h2>

          {/* Client Name */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Client Name *</label>
            <input
              type="text"
              name="clientName"
              value={formData.clientName}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter client name"
              required
            />
          </div>

          {/* Projects */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Projects *</label>
            <div className="flex items-center space-x-3 mb-4">
              <input
                type="text"
                value={projectInput || ''}
                onChange={(e) => setProjectInput(e.target.value)}
                placeholder="Enter project name"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => {
                  if (!projectInput?.trim()) return;
                  setFormData((prev) => ({
                    ...prev,
                    projects: [...prev.projects, projectInput.trim()]
                  }));
                  setProjectInput('');
                }}
                className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                title="Add Project"
              >
                <Plus size={16} />
              </button>
            </div>

            {formData.projects.length > 0 && (
              <ul className="space-y-2">
                {formData.projects.map((proj, idx) => (
                  <li key={idx} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-2">
                    <span className="text-sm text-gray-800">{proj}</span>
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          projects: prev.projects.filter((_, i) => i !== idx)
                        }))
                      }
                      className="text-gray-400 hover:text-red-600"
                      title="Remove"
                    >
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        
        {/* Permissions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">
            Employee Permissions
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {permissions.map(permission => (
              <div key={permission.id} className="flex items-start space-x-3">
                <div className="flex items-center h-5">
                  <input
                    id={permission.id}
                    type="checkbox"
                    checked={formData.permissions[permission.id] || false}
                    onChange={() => handlePermissionToggle(permission.id)}
                    className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                  />
                </div>
                <div className="text-sm">
                  <label htmlFor={permission.id} className="font-medium text-gray-900 cursor-pointer">
                    {permission.label}
                  </label>
                  <p className="text-gray-500">{permission.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Default Password Notice */}
        {!isEditing && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <AlertCircle size={20} className="text-blue-600" />
              <div>
                <p className="text-blue-800 font-medium">Default Password Information</p>
                <p className="text-blue-700 text-sm">
                  The employee will be created with the default password: <strong>12345678</strong>
                  <br />
                  They can change this password after their first login.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/employees')}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
            disabled={submitting}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
            {submitting ? (
                <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>{isEditing ? 'Updating...' : 'Creating...'}</span>
                </>
              ) : (
                <>
                <Save size={18} />
                <span>{isEditing ? 'Update Employee' : 'Create Employee'}</span>
                </>
              )}
            </button>
          </div>
      </form>
        </div>
  );
};

export default EmployeeForm; 